import { ReactNode, isValidElement } from 'react';
import { Theme } from '@mui/material';
import type { SystemStyleObject } from '@mui/system';

import { SxPropsOverrides } from '../../types/markup';

/**
 * Retrieves a child element from children based on a specified key.
 *
 * @param children - The array of React nodes or a single React node.
 * @param key - The key to match against the child elements.
 * @returns The matching child element or null if not found.
 */
export const getChildByKey = (
  children: ReactNode | ReactNode[],
  key: string,
): ReactNode | null => {
  let result: ReactNode | null = null;

  if (Array.isArray(children)) {
    result =
      children.find((child) => isValidElement(child) && child.key === key) ??
      null;
  } else if (isValidElement(children) && children.key === key) {
    result = children;
  }

  return result;
};

/**
 * Properly resolve sx properties, either as a function or as an object.
 * To be used to pass down sx prop to MUI components.
 *
 * @param sxProps - The style properties or a function that returns style properties.
 * @param theme - The theme object used to apply the style properties if `sxProps` is a function.
 * @returns The resolved style properties.
 * @throws Will throw an error if `sxProps` is a function and `theme` is not provided.
 */
export const applySxOverrides = (
  sxProps: SxPropsOverrides | undefined,
  theme?: Theme,
): SystemStyleObject<Theme> => {
  if (typeof sxProps === 'function' && !theme) {
    throw new Error(
      '[Markup] Theme is required when passing a sxProps as a function',
    );
  }

  return typeof sxProps === 'function' && theme
    ? sxProps(theme)
    : (sxProps as SystemStyleObject<Theme>);
};
